import { useEffect, useRef, useState } from "react";
import { Form, Input, Select, InputNumber, Row, Col, Card, App, Button, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useSelect } from "@refinedev/antd";
import { useCustomMutation } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { usePageTitle } from "../../hooks";
import { MapWithDraw } from "./components/MapWithDraw";

// PR3 — Eliminamos el selector de variedad a nivel finca (`Farm.variety`).
// La variedad vive ahora en cada parcela declarada (Plot.variety) y se
// pide en `MapWithDraw`. La constante anterior queda como histórico
// hasta PR7, que también borrará el campo `Farm.variety` en BD.

export const FarmCreate = () => {
  const { t } = useTranslation();
  const { id: producerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const { mutate } = useCustomMutation();
  usePageTitle(t("producers.farms.actions.create", "Crear Finca"));

  const referencePlotsRef = useRef<
    Array<{
      id?: number;
      geom: GeoJSON.Geometry;
      surface: number;
      estimated_planting_date?: string;
      poligono?: number | null;
      parcela?: number | null;
      recinto?: number | null;
      // PR3 — Variedad protegida obligatoria por parcela.
      variety?: number | null;
    }>
  >([]);

  const { selectProps: countrySelectProps } = useSelect({
    resource: "countries",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const selectedCountry = Form.useWatch("country", form);
  const selectedProvince = Form.useWatch("province", form);

  const { selectProps: provinceSelectProps } = useSelect({
    resource: "v1/provinces",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
    filters: selectedCountry
      ? [{ field: "country", operator: "eq", value: selectedCountry }]
      : [],
    queryOptions: { enabled: !!selectedCountry },
  });

  const { selectProps: townSelectProps } = useSelect({
    resource: "v1/towns",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 2000 },
    filters: selectedProvince
      ? [{ field: "province", operator: "eq", value: selectedProvince }]
      : [],
    queryOptions: { enabled: !!selectedProvince },
  });

  // Clear province + town when country changes, clear town when province changes
  const prevCountryRef = useRef(selectedCountry);
  const prevProvinceRef = useRef(selectedProvince);
  useEffect(() => {
    if (prevCountryRef.current !== undefined && prevCountryRef.current !== selectedCountry) {
      form.setFieldsValue({ province: undefined, town: undefined });
    }
    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, form]);
  useEffect(() => {
    if (prevProvinceRef.current !== undefined && prevProvinceRef.current !== selectedProvince) {
      form.setFieldValue("town", undefined);
    }
    prevProvinceRef.current = selectedProvince;
  }, [selectedProvince, form]);

  const selectFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // PR3 — Validación cliente: cada parcela debe traer variedad.
      const plotsMissingVariety = referencePlotsRef.current.filter(
        (p) => p.variety == null,
      );
      if (plotsMissingVariety.length > 0) {
        setSubmitting(false);
        message.error(
          t(
            "producers.farms.messages.plot_variety_required",
            "Cada parcela debe tener una variedad protegida asignada ({{n}} sin asignar).",
            { n: plotsMissingVariety.length },
          ),
        );
        return;
      }

      const payload = {
        ...values,
        reference_plots: referencePlotsRef.current.map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          geom: p.geom,
          surface: p.surface,
          estimated_planting_date: p.estimated_planting_date || null,
          poligono: p.poligono ?? null,
          parcela: p.parcela ?? null,
          recinto: p.recinto ?? null,
          variety: p.variety ?? null,
        })),
      };

      mutate(
        {
          url: `/v1/producers/${producerId}/farms/`,
          method: "post",
          values: payload,
        },
        {
          onSuccess: () => {
            setSubmitting(false);
            message.success(t("producers.farms.messages.created_success", "Finca creada correctamente"));
            navigate(`/producers/show/${producerId}`);
          },
          onError: () => {
            setSubmitting(false);
          },
        },
      );
    } catch {
      // validation errors shown inline
    }
  };

  return (
    <Card
      title={t("producers.farms.actions.create", "Crear Finca")}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/producers/show/${producerId}`)}>
          {t("common.back", "Volver")}
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={t("producers.farms.fields.name", "Nombre")}
              name="name"
              rules={[{ required: true, message: t("producers.farms.messages.name_required", "El nombre de la finca es obligatorio") }]}
            >
              <Input placeholder={t("producers.farms.placeholders.name", "Nombre de la finca")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.country", "País")} name="country">
              <Select {...countrySelectProps} placeholder={t("common.select", "Seleccionar")} allowClear showSearch filterOption={selectFilter} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.province", "Provincia")} name="province">
              <Select
                {...provinceSelectProps}
                placeholder={selectedCountry ? t("common.select", "Seleccionar") : t("producers.farms.placeholders.select_country_first", "Seleccione primero un país")}
                allowClear
                showSearch
                filterOption={selectFilter}
                disabled={!selectedCountry}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.town", "Municipio")} name="town">
              <Select
                {...townSelectProps}
                placeholder={selectedProvince ? t("common.select", "Seleccionar") : t("producers.farms.placeholders.select_province_first", "Seleccione primero una provincia")}
                allowClear
                showSearch
                filterOption={selectFilter}
                disabled={!selectedProvince}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t("producers.farms.fields.surface_tango", "Superficie Tango")} name="surface_tango">
              <InputNumber style={{ width: "100%" }} min={0} step={0.01} decimalSeparator="," placeholder={t("producers.farms.placeholders.surface_tango", "Ej: 15,50")} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t("producers.farms.reference_plots", "Parcelas de Referencia")}>
          <div style={{ position: "relative", zIndex: 0 }}>
          <MapWithDraw
            onPlotsChange={(plots) => { referencePlotsRef.current = plots; }}
            height="600px"
          />
          </div>
        </Form.Item>
      </Form>

      <div style={{ textAlign: "right", marginTop: 16 }}>
        <Space>
          <Button onClick={() => navigate(`/producers/show/${producerId}`)}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      </div>
    </Card>
  );
};
